import {
    DialogDistributionChannel, DialogFlowLogicGateMatchRule, DialogFlowLogicGateValueSource, DialogFlowMultiOptionDataSource, DialogFlowTemplateMessageConversionRule,
    DialogFlowTemplateMessageType, DialogScheduleRhytm, PublicExternalLinkType, RequestStatus, defaults, entities
} from 'typed/api';
import { deepAssign } from 'typed/utils';
import {
    childrenReducer, combineReducers, entityAddAllReducer, entityAddReducer, entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer, entityEditReducer,
    entityLoadAllReducer, entityLoadManyReducer, entityLoadReducer, entityVoidAllReducer, sortableEntitiesReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { ActionType } from 'utils/constants';

const
    s = defaults.DialogScheduleSettingsReport,
    ss = defaults.NetworkArrayOf(s),
    sfs: entities.DialogScheduleFlow[] = [],
    sus: entities.DialogScheduleUser[] = [],
    sbs: entities.DialogScheduleBatchReport[] = [],
    t = defaults.DialogFlowTemplateReport,
    ts = defaults.NetworkArrayOf(t),
    tm = defaults.DialogFlowTemplateMessageReport,
    tms: entities.DialogFlowTemplateMessageReport[] = [],
    tmos: entities.DialogFlowTemplateMessageOption[] = [],
    modsa = defaults.DialogFlowMultiOptionDataSource,
    modsas = defaults.NetworkArrayOf(modsa),
    dfss = defaults.NetworkArrayOf(defaults.DialogFlowSession);

export default combineReducers({
    dialogScheduleSettings: sortedEntitiesReducer(ss, 'Name', switchReducers(ss,
        entityLoadAllReducer(ss, 'LOAD_ALL_DIALOG_SCHEDULE_SETTINGS'),
        entityAddReducer(ss, 'ADD_DIALOG_SCHEDULE_SETTING'),
        entityEditReducer(ss, 'EDIT_DIALOG_SCHEDULE_SETTING'),
        childrenReducer(ss,
            combineReducers(s, {
                Flows: switchReducers(sfs,
                    entityLoadManyReducer(sfs, 'LOAD_MANY_DIALOG_SCHEDULE_FLOWS'),
                    entityAddAllReducer(sfs, 'ADD_ALL_DIALOG_SCHEDULE_FLOWS'),
                    entityEditAllReducer(sfs, 'EDIT_ALL_DIALOG_SCHEDULE_FLOWS'),
                    entityDeleteAllReducer(sfs, 'DELETE_ALL_DIALOG_SCHEDULE_FLOWS')
                ),
                Users: switchReducers(sus,
                    entityAddAllReducer(sus, 'ADD_ALL_DIALOG_SCHEDULE_USERS'),
                    entityDeleteAllReducer(sus, 'DELETE_ALL_DIALOG_SCHEDULE_USERS')
                ),
                Batches: sortedEntitiesReducer(sbs, 'StartDate', switchReducers(sbs,
                    entityLoadReducer(sbs, 'LOAD_DIALOG_SCHEDULE_BATCH'),
                    entityAddReducer(sbs, 'ADD_DIALOG_SCHEDULE_BATCH'),
                    entityEditReducer(sbs, 'EDIT_DIALOG_SCHEDULE_BATCH'),
                    entityDeleteReducer(sbs, 'DELETE_DIALOG_SCHEDULE_BATCH'),
                    (state, action) => {
                        let nextState = state || sbs;

                        if (action.type === 'SEND_DIALOG_SCHEDULE_BATCH' ||
                            action.type === 'SEND_DIALOG_SCHEDULE_BATCH_TO_MICROSOFT_TEAMS' ||
                            action.type === 'SEND_DIALOG_SCHEDULE_BATCH_TO_WHATSAPP' ||
                            action.type === 'CREATE_DIALOG_SCHEDULE_BATCH_RESULT_WORKSHOP' ||
                            action.type === 'CREATE_DIALOG_SCHEDULE_BATCH_RESULT_STRATEGY') {
                            nextState = deepAssign(nextState, { [nextState.getIndexByID(action.payload.entityID)]: { Custom: { status: action.payload.status } } });
                        }

                        if (action.type === 'SEND_DIALOG_SCHEDULE_BATCH' && action.payload.status === RequestStatus.SUCCEEDED) {
                            nextState = deepAssign(nextState, { [nextState.getIndexByID(action.payload.entityID)]: { IsSent: true } });
                        }

                        if (action.type === 'CREATE_DIALOG_SCHEDULE_BATCH_RESULT_WORKSHOP' && action.payload.status === RequestStatus.SUCCEEDED) {
                            nextState = deepAssign(nextState, {
                                [nextState.getIndexByID(action.payload.entityID)]: {
                                    IsClosed: action.payload.closeBatch,
                                    ResultWorkshopID: action.payload.response || null
                                }
                            });
                        }

                        return nextState;
                    }
                ))
            }),
            (state, action) => state.Data.getIndexByID(action.payload?.parentID)
        ),
        entityDeleteReducer(ss, 'DELETE_DIALOG_SCHEDULE_SETTING'),
        (state, action) => {
            const nextState = state || ss;

            if ((action.type === 'ADD_DIALOG_SCHEDULE_BATCH' || action.type === 'EDIT_DIALOG_SCHEDULE_BATCH' || action.type === 'DELETE_DIALOG_SCHEDULE_BATCH') &&
                action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(({ Batches }) => ({
                        LatestBatchStartDate: Batches.filter(({ StartDate }) => +StartDate <= +new Date()).sortBy('StartDate').last()?.StartDate || null
                    }))
                });
            }

            if (action.type === 'DELETE_DIALOG_FLOW_TEMPLATE' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(({ Flows }) => ({ Flows: Flows.filter(({ DialogFlowTemplateID }) => DialogFlowTemplateID !== action.payload.entityID) }))
                });
            }

            if (action.type === ActionType.UNLINK_AND_PEND_TENANT_FOR_MICROSOFT_TEAMS_BOT_LINKED_CONVERSATIONS_SUCCESS) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(() => ({
                        DistributionChannelID: DialogDistributionChannel.EMAIL,
                        MicrosoftTeamsBotConversationID: null
                    }))
                });
            }

            if (action.type === 'DELETE_AGENDA_THEME' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(({ Batches }) => ({
                        Batches: Batches.map(({ AgendaThemeID }) => ({
                            AgendaThemeID: AgendaThemeID !== action.payload.entityID ? AgendaThemeID : null
                        }))
                    }))
                });
            }

            if (action.type === 'ADD_PUBLIC_EXTERNAL_LINK' && action.payload.entity.TypeID === PublicExternalLinkType.DIALOG && action.payload.entity.DialogScheduleBatchID) {
                const schedule = nextState.Data.find(({ Batches }) => !!Batches.getByID(action.payload.entity.DialogScheduleBatchID));

                if (schedule) {
                    return deepAssign(nextState, {
                        Data: {
                            [nextState.Data.getIndexByID(schedule?.ID)]: {
                                Batches: {
                                    [schedule.Batches.getIndexByID(action.payload.entity.DialogScheduleBatchID)]: {
                                        Custom: {
                                            status: action.payload.status
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            }

            if (action.type === 'DELETE_STRATEGY_DESCRIPTION' && action.payload.status === RequestStatus.SUCCEEDED) {
                if (action.payload.deleteSubstrategies) {
                    return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
                }
                else {
                    return deepAssign(nextState, {
                        Data: nextState.Data
                            .filter(({ StrategyDescriptionID }) => StrategyDescriptionID !== action.payload.entityID)
                            .map(({ Batches }) => ({ Batches: Batches.filter(({ StrategyDescriptionID }) => StrategyDescriptionID !== action.payload.entityID) }))
                    });
                }
            }

            return nextState;
        },
        entityVoidAllReducer(ss, 'VOID_ALL_DIALOG_SCHEDULE_SETTINGS')
    )),
    dialogScheduleRhytms: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.DialogScheduleRhytm),
        'LOAD_ALL_DIALOG_SCHEDULE_RHYTMS',
        DialogScheduleRhytm as any,
        'DialogScheduleRhytm'
    ),
    dialogDistributionChannels: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.DialogDistributionChannel),
        'LOAD_ALL_DIALOG_DISTRIBUTION_CHANNELS',
        DialogDistributionChannel as any,
        'DialogDistributionChannel'
    ),
    dialogFlowTemplates: sortedEntitiesReducer(ts, 'Name', switchReducers(ts,
        entityLoadAllReducer(ts, 'LOAD_ALL_DIALOG_FLOW_TEMPLATES'),
        entityAddReducer(ts, 'ADD_DIALOG_FLOW_TEMPLATE'),
        entityEditReducer(ts, 'EDIT_DIALOG_FLOW_TEMPLATE'),
        (state, action) => {
            const nextState = state || ts;

            if (action.type === 'STORE_DIALOG_FLOW_TEMPLATE_SCREENSHOT') {
                let entity = nextState.Data.getByID(action.payload.entityID);

                if (entity) {
                    entity = deepAssign(entity, { Custom: { status: action.payload.status } });

                    if (action.payload.status === RequestStatus.PENDING) { entity = deepAssign(entity, { PreviewImageTaken: new Date() }); }
                    if (action.payload.status === RequestStatus.SUCCEEDED) { entity = deepAssign(entity, { PreviewBlobStorageUrl: action.payload.response }); }

                    return deepAssign(nextState, { Data: { [nextState.Data.getIndexByID(entity.ID)]: entity } });
                }
            }

            if (action.type === 'ADD_DIALOG_SCHEDULE_SETTING' && action.payload.useSystemTemplates && action.payload.status === RequestStatus.SUCCEEDED ||
                action.type === 'CREATE_PREVIEW_DIALOG_FLOW_SESSION' && action.payload.useSystemTemplate && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
            }

            if ((action.type === 'ADD_ALL_DIALOG_SCHEDULE_FLOWS' || action.type === 'EDIT_ALL_DIALOG_SCHEDULE_FLOWS') && action.payload.status === RequestStatus.SUCCEEDED &&
                (action.payload.useSystemTemplates || (action.payload.entities as entities.DialogScheduleFlow[]).some(({ DialogFlowTemplateID }) => !DialogFlowTemplateID))) {
                return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
            }

            return nextState;
        },
        childrenReducer(ts,
            combineReducers(t, {
                Messages: switchReducers(tms,
                    entityLoadReducer(tms, 'LOAD_DIALOG_FLOW_TEMPLATE_MESSAGE'),
                    entityAddReducer(tms, 'ADD_DIALOG_FLOW_TEMPLATE_MESSAGE'),
                    entityEditAllReducer(tms, 'EDIT_ALL_DIALOG_FLOW_TEMPLATE_MESSAGES'),
                    entityEditReducer(tms, 'EDIT_DIALOG_FLOW_TEMPLATE_MESSAGE'),
                    childrenReducer(tms,
                        combineReducers(tm, {
                            Options: sortableEntitiesReducer(tmos, null, switchReducers(tmos,
                                entityAddAllReducer(tmos, 'ADD_ALL_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTIONS'),
                                entityAddReducer(tmos, 'ADD_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTION'),
                                entityEditAllReducer(tmos, 'EDIT_ALL_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTIONS'),
                                entityEditReducer(tmos, 'EDIT_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTION'),
                                entityDeleteAllReducer(tmos, 'DELETE_ALL_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTIONS'),
                                entityDeleteReducer(tmos, 'DELETE_DIALOG_FLOW_TEMPLATE_MESSAGE_OPTION')
                            ))
                        }),
                        (state, action) => state.getIndexByID((action.payload as any)?.parentID)
                    ),
                    (state, action) => {
                        let newState = state || tms;

                        if (action.type === 'ADD_DIALOG_FLOW_TEMPLATE_MESSAGE' && action.payload.status === RequestStatus.SUCCEEDED) {
                            newState = deepAssign(newState, {
                                [newState.getIndexByID(action.payload.response)]: {
                                    Options: action.payload.entity.Options.map((option: entities.DialogFlowTemplateMessageOption) => ({
                                        ...option, FromMessageID: action.payload.response
                                    }))
                                }
                            });

                            if (action.payload.parentMessageID) {
                                newState = deepAssign(newState, {
                                    [newState.getIndexByID(action.payload.parentMessageID)]: {
                                        NextMessageID: action.payload.response
                                    }
                                });
                            }

                            if (action.payload.parentOptionID) {
                                newState = newState.map(message => deepAssign(message, {
                                    Options: message.Options.map((option, _, options) => {
                                        let nextMessageID = option.NextMessageID;

                                        if (option.ID === action.payload.parentOptionID ||
                                            message.MessageTypeID === DialogFlowTemplateMessageType.USER_MULTI_SELECT &&
                                            option.IsExitOption === options.getByID(action.payload.parentOptionID)?.IsExitOption) {
                                            nextMessageID = action.payload.response;
                                        }

                                        return { NextMessageID: nextMessageID };
                                    })
                                }));
                            }

                            if (action.payload.entity.ConversionRuleID === DialogFlowTemplateMessageConversionRule.USER_ROLE) {
                                newState = newState.map((message) => {
                                    if (message.ConversionRuleID !== DialogFlowTemplateMessageConversionRule.USER_ROLE ||
                                        message.ID === action.payload.response) {
                                        return message;
                                    }

                                    return deepAssign(message, { ConversionRuleID: DialogFlowTemplateMessageConversionRule.IGNORE });
                                });
                            }

                            return newState;
                        }

                        if (action.type === 'EDIT_DIALOG_FLOW_TEMPLATE_MESSAGE' && action.payload.status === RequestStatus.SUCCEEDED) {
                            if (action.payload.entity.MessageTypeID !== DialogFlowTemplateMessageType.USER_MULTI_OPTION &&
                                action.payload.entity.MessageTypeID !== DialogFlowTemplateMessageType.USER_MULTI_SELECT &&
                                action.payload.entity.MessageTypeID !== DialogFlowTemplateMessageType.LOGIC_GATE) {
                                newState = deepAssign(newState, {
                                    [newState.getIndexByID(action.payload.entityID)]: {
                                        Options: []
                                    }
                                });
                            }

                            if (action.payload.entity.ConversionRuleID === DialogFlowTemplateMessageConversionRule.USER_ROLE) {
                                newState = newState.map((message) => {
                                    if (message.ConversionRuleID !== DialogFlowTemplateMessageConversionRule.USER_ROLE ||
                                        message.ID === action.payload.entityID) {
                                        return message;
                                    }

                                    return deepAssign(message, { ConversionRuleID: DialogFlowTemplateMessageConversionRule.IGNORE });
                                });
                            }

                            return newState;
                        }

                        return newState;
                    },
                    entityDeleteReducer(tms, 'DELETE_DIALOG_FLOW_TEMPLATE_MESSAGE'),
                    (state, action) => {
                        const newState = state || tms;

                        if (action.type === 'DELETE_DIALOG_FLOW_TEMPLATE_MESSAGE' && action.payload.status === RequestStatus.SUCCEEDED) {
                            return newState.map(message => deepAssign(message, {
                                SourceMessageID: message.SourceMessageID !== action.payload.entityID ? message.SourceMessageID : null,
                                NextMessageID: message.NextMessageID !== action.payload.entityID ? message.NextMessageID : null,
                                Options: message.Options.map(({ NextMessageID }) => ({
                                    NextMessageID: NextMessageID !== action.payload.entityID ? NextMessageID : null
                                }))
                            }));
                        }

                        return newState;
                    }
                )
            }),
            (state, action) => state.Data.getIndexByID((action.payload as any)?.grandParentID || (action.payload as any)?.parentID)
        ),
        entityDeleteReducer(ts, 'DELETE_DIALOG_FLOW_TEMPLATE')
    )),
    dialogFlowSystemTemplates: entityLoadAllReducer(ts, 'LOAD_ALL_DIALOG_FLOW_SYSTEM_TEMPLATES'),
    dialogFlowTemplateMessageTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.DialogFlowTemplateMessageType),
        'LOAD_ALL_DIALOG_FLOW_MESSAGE_TYPES',
        DialogFlowTemplateMessageType as any,
        'DialogFlowTemplateMessageType'
    ),
    dialogFlowTemplateMessageConversionRules: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.DialogFlowTemplateMessageConversionRule),
        'LOAD_ALL_DIALOG_FLOW_MESSAGE_CONVERSION_RULES',
        DialogFlowTemplateMessageConversionRule as any,
        'DialogFlowTemplateMessageConversionRule'
    ),
    dialogFlowLogicGateValueSources: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.DialogFlowLogicGateValueSource),
        'LOAD_ALL_DIALOG_FLOW_LOGIC_GATE_VALUE_SOURCES',
        DialogFlowLogicGateValueSource as any,
        'DialogFlowLogicGateValueSource'
    ),
    dialogFlowLogicGateMatchRules: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.DialogFlowLogicGateMatchRule),
        'LOAD_ALL_DIALOG_FLOW_LOGIC_GATE_MATCH_RULES',
        DialogFlowLogicGateMatchRule as any,
        'DialogFlowLogicGateMatchRule'
    ),
    dialogFlowMultiOptionDataSources: switchReducers(modsas,
        synchronizableEnumReducer(
            defaults.NetworkArrayOf(defaults.DialogFlowMultiOptionDataSource),
            'LOAD_ALL_DIALOG_FLOW_MULTI_OPTION_DATA_SOURCES',
            DialogFlowMultiOptionDataSource as any,
            'DialogFlowMultiOptionDataSource'
        ),
        childrenReducer(modsas,
            combineReducers(modsa, {
                PreviewOptions: (state, action) => {
                    let nextState = state || modsa.PreviewOptions;

                    if (action.type === 'LOAD_DIALOG_FLOW_MULTI_OPTION_DATA_SOURCE_OPTIONS') {
                        nextState = deepAssign(nextState, { Custom: { status: action.payload.status } });

                        if (action.payload.status === RequestStatus.SUCCEEDED) {
                            nextState = deepAssign(nextState, {
                                Data: JSON.parse(action.payload.response) as unknown as { ID: number; Name: string; }[]
                            });
                        }
                    }

                    return nextState;
                }
            }),
            (state, action) => state.Data.getIndexByID((action.payload as any)?.entityID)
        )
    ),
    dialogFlowSessions: switchReducers(dfss,
        entityLoadReducer(dfss, 'LOAD_DIALOG_FLOW_SESSION'),
        (state: entities.NetworkArray<entities.DialogFlowSession> | undefined, action) => {
            const newState = state || dfss;

            if (action.type === 'CREATE_PREVIEW_DIALOG_FLOW_SESSION' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(newState, {
                    Data: newState.Data.copy().concat(action.payload.response)
                });
            }

            return newState;
        }
    )
});
